import React, { useState, useEffect } from "react";
import { 
  Box,
  Heading,
  Flex,
  Image,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Divider,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel
} from "@chakra-ui/react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale,  BarElement, Tooltip, Legend } from "chart.js";
import { useParams } from "react-router-dom";
import axios from "../../axiosConfig";
import { CoverIcon } from "../../assets/icons";
import { useTranslation } from "react-i18next";
import { formatOrganizationSurveyTitle } from "../../utils/organizationSurveyHelper";
import ReportSurveyHeader from "./ReportSurveyHeader";

// Register Chart Components
ChartJS.register(CategoryScale, /* LinearScale, */ BarElement, Tooltip, Legend);

const DEC_HOUSES = 2;

const Overview = () => {
  const { t, i18n } = useTranslation();
  const { organizationSurveyId } = useParams();
  const [stabilityData, setStabilityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

   const imageSrc = i18n.language === "sv"
      ? "/images/IPO-Diagram-sv.webp"
      : "/images/IPO-Diagram-en.webp";

  useEffect(() => {
    const fetchStabilityData = async () => {
      try {
        const response = await axios.get(`/survey_reports/${organizationSurveyId}/stability_overview`);
        setStabilityData(response.data);
      } catch (err) {
        setError(t("reports.overview.error_loading"));
      } finally {
        setLoading(false);
      }
    };

    fetchStabilityData();
  }, [organizationSurveyId]);

  if (loading) return <Spinner size="xl" />;
  if (error) return <Text color="red.500">{error}</Text>;

  const getValidNumber = (value) => (typeof value === "number" && !isNaN(value) ? value : "N/A");

  // 📊 Bar Chart - Score per Area
  const barData = {
    labels: stabilityData.areas.map((area) => t(area.name)), 
    datasets: [
      {
        label: t("reports.overview.response_consistency"),
        data: stabilityData.areas.map((area) => area.stability_index),
        backgroundColor: "rgba(54, 162, 235, 0.6)",
      },
      // Commented out benchmark temporarily
      // {
      //   label: t("reports.overview.benchmark_consistency"),
      //   data: stabilityData.areas.map((area) => area.benchmark_stability_index),
      //   backgroundColor: "rgba(255, 99, 132, 0.6)",
      // },
    ],
  };

  return (
    <Box>
      <ReportSurveyHeader survey={stabilityData} />

      <Flex align="center" mb={4}>
        <CoverIcon />
        <Heading as="h1" size="2xl">{t("reports.cover.title")}</Heading>
      </Flex>

      <Accordion defaultIndex={[0, 1]} allowMultiple width="100%" mt={6}>
        {/* Section 1: IPO Framework */}
        <AccordionItem>
          <AccordionButton>
            <Box flex="1" textAlign="left" fontWeight="bold">
              {t("reports.introduction.ipo_title")}
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel>
            <Box width="100%" display="flex" justifyContent="center" py={5} px={{ base: 2, md: 10, lg: 20 }}>
              <Image
                src={imageSrc}
                alt={t("reports.introduction.ipo_alt")}
                maxW="100%"
                height="auto"
              />
            </Box>
            <Text mb={4}>{t("reports.introduction.ipo_explanation_1")}</Text>
            <Text mb={4}>{t("reports.introduction.ipo_explanation_2")}</Text>
            <Text>{t("reports.introduction.ipo_explanation_3")}</Text>
          </AccordionPanel>
        </AccordionItem>

        {/* Section 2: Result */}
        <AccordionItem>
          <AccordionButton>
            <Box flex="1" textAlign="left" fontWeight="bold">
              {t("reports.overview.result")}
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel>
            <Text fontSize={{ base: "md", md: "2xl" }} color="gray.600" mb={2} mt={6}>
              {t("reports.cover.survey")}: {formatOrganizationSurveyTitle(stabilityData)}
            </Text>
            <Text fontSize={{ base: "md", md: "xl" }} color="gray.500" mb={4}>
              {t("reports.cover.organization")}: {stabilityData.organizationName}
            </Text>

            <Divider />

            {/* Overall Score */}
            <Box bg="gray.100" p={6} borderRadius="md" w="full" my={4}>
              <Text fontSize="2xl" fontWeight="bold">
                {t("reports.overview.overall_index")}: {getValidNumber(stabilityData.overallStabilityIndex)}
              </Text>
            </Box>

            <Divider />

            <Box maxW="700px" mx="auto">
              <Bar data={barData} />
            </Box>
          </AccordionPanel>
        </AccordionItem>

        {/* Section 3: Result */}
        <AccordionItem>
          <AccordionButton>
            <Box flex="1" textAlign="left" fontWeight="bold">
              {t("reports.overview.data_title")}
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel>
            <Box maxW="700px" mx="auto" mt={6}>
              <Table variant="striped" size="sm" mt={4}>
                <Thead>
                  <Tr>
                    <Th>{t("reports.overview.team")}</Th>
                    <Th>{t("reports.overview.index")}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {stabilityData.roles.map((role) => (
                    <Tr key={role.role}>
                      <Td>{t(`reports.area_report.table_headers.${role.role.toLowerCase()}`, role.role)}</Td>
                      <Td title={getValidNumber(role.stability_index)}>{getValidNumber(role.stability_index).toFixed(DEC_HOUSES)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>      
    </Box>
  );
};

export default Overview;
